import {
  DataGrid,
  ErrorState,
  type GridEventListener,
  type GridRowParams,
  NoRowsOverlay,
} from '@shared/components';
import { ORDER_TYPE } from '@shared/graphql';
import { useSearchUsersByAppRoleFiltersQuery } from '@user-management/graphql';

import { useUserManagementListSearchParams } from '../../hooks';
import { useUserManagementListModal } from '../../providers';

import { columns } from './columns';
import { mapper } from './mapper';

export default function UserManagementList() {
  const [
    { search, sortBy, sortOrder, page, pageSize, status, role },
    { changePageNumber, changePageSize, changeSorting },
  ] = useUserManagementListSearchParams();

  const { setAction } = useUserManagementListModal();

  const {
    data,
    loading: isLoading,
    error,
  } = useSearchUsersByAppRoleFiltersQuery({
    variables: {
      order: {
        field: sortBy,
        type: ORDER_TYPE[sortOrder],
      },
      paging: {
        pageSize,
        currentPage: page,
      },
      search: {
        firstname: search,
        lastname: search,
        email: search,
        status: status ?? undefined,
      },
      appRoleNames: role,
    },
  });

  const users = data?.UsBoSearchUsersWithAppRoleFilters?.users ?? [];
  const total = data?.UsBoSearchUsersWithAppRoleFilters?.total ?? 0;

  const mappedUsers = mapper(users);

  const handleRowClick: GridEventListener<'rowClick'> = (params: GridRowParams) => {
    const { row } = params;
    setAction('edit', row.id);
  };

  if (error) {
    return <ErrorState />;
  }

  return (
    <DataGrid
      columns={columns}
      rows={mappedUsers}
      loading={isLoading}
      slots={{
        noRowsOverlay: NoRowsOverlay,
      }}
      onRowClick={handleRowClick}
      disableColumnMenu
      slotProps={{
        footer: {
          total: total,
          pagination: {
            page: page,
            count: total,
            rowsPerPage: pageSize,
            onPageChange: (_, page) => {
              changePageNumber(page);
            },
            onRowsPerPageChange: (event) => {
              changePageSize(parseInt(event.target.value));
            },
          },
        },
      }}
      onSortModelChange={(sorting) => {
        const sortModel = sorting[0];

        if (sortModel?.sort && sortModel?.field) {
          return changeSorting({
            sortOrder: sortModel.sort,
            sortBy: sortModel.field,
          });
        }

        return changeSorting({
          sortOrder: null,
          sortBy: null,
        });
      }}
      sx={(theme) => ({
        backgroundColor: theme.palette.background.paper,
        '--DataGrid-overlayHeight': '412px',
        '& .MuiDataGrid-cell': {
          padding: theme.spacing(3),
        },
        '& .MuiDataGrid-columnHeaders': {
          backgroundColor: theme.palette.grey[100],
          borderBottom: `1px solid ${theme.palette.grey[300]}`,
        },
        '& .MuiDataGrid-row > .MuiDataGrid-withBorderColor': {
          borderBottom: `1px solid ${theme.palette.grey[300]}`,
        },
      })}
    />
  );
}
